import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Paper, Chip, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Alert, Button, IconButton, Tooltip, TextField, Dialog, DialogTitle, DialogContent, DialogActions, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import { transactionStore, BusinessKeywordChange } from '../utils/transactionStore';
interface Keyword {
  id: string;
  keyword: string;
  addedBy: string;
  addedOn: string;
}
const mkKeyword = (kw: string, i: number): Keyword => ({
  id: `kw-${i}`,
  keyword: kw,
  addedBy: 'System',
  addedOn: '2026-07-24'
});
const INITIAL_KEYWORDS: Keyword[] = ['CO', 'COMPANY', 'CORP', 'CORPORATION', 'PVT', 'PRIVATE', 'LTD', 'LIMITED', 'INC', 'INCORPORATED', 'LLC', 'PARTNERSHIP', 'ENTERPRISE', 'SOLUTIONS', 'TRADING', 'INDUSTRIES'].map((k, i) => mkKeyword(k, i));
const STATUS_CHIP: Record<string, {
  label: string;
  bg: string;
  color: string;
}> = {
  PENDING_CHECKER: {
    label: 'Pending Deletion',
    bg: 'rgba(245,158,11,0.15)',
    color: '#D97706'
  },
  REJECTED: {
    label: 'Deletion Rejected',
    bg: 'rgba(239,68,68,0.12)',
    color: '#EF4444'
  }
};
interface Props {
  onLogout: () => void;
}
const BusinessKeywordConfig: React.FC<Props> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keywords, setKeywords] = useState<Keyword[]>(INITIAL_KEYWORDS);
  const [pendingRequests, setPendingRequests] = useState<BusinessKeywordChange[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newKw, setNewKw] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Keyword | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  useEffect(() => {
    const sync = () => {
      const all = transactionStore.getAllBusinessKeywordChanges();

      // Apply approved ADD requests (turn into a real keyword row) once, and drop them from the queue view
      const approvedAdds = all.filter(c => c.action === 'ADD' && c.status === 'APPROVED');
      approvedAdds.forEach(c => {
        setKeywords(prev => prev.some(k => k.keyword === c.keyword) ? prev : [...prev, {
          id: `kw-${c.id}`,
          keyword: c.keyword,
          addedBy: c.checkerActedBy || c.requestedBy,
          addedOn: (c.checkerActedAt || c.requestedAt).split('T')[0]
        }]);
      });

      // Apply approved DELETE requests (remove the real keyword row)
      const approvedDeleteIds = all.filter(c => c.action === 'DELETE' && c.status === 'APPROVED').map(c => c.keywordId);
      if (approvedDeleteIds.length) {
        setKeywords(prev => prev.filter(k => !approvedDeleteIds.includes(k.id)));
      }
      setPendingRequests(all.filter(c => c.status === 'PENDING_CHECKER' || c.status === 'REJECTED'));
    };
    sync();
    const unsubscribe = transactionStore.subscribe(sync);
    return unsubscribe;
  }, []);
  const handleAdd = () => {
    if (!newKw.trim()) return;
    const keyword = newKw.trim().toUpperCase();
    transactionStore.submitBusinessKeywordChange({
      action: 'ADD',
      keyword
    }, `Addition of keyword "${keyword}" requested via Business Entity Keywords`);
    setNewKw('');
    setAddOpen(false);
  };
  const handleDelete = (kw: Keyword) => {
    transactionStore.submitBusinessKeywordChange({
      action: 'DELETE',
      keyword: kw.keyword,
      keywordId: kw.id
    }, `Deletion of keyword "${kw.keyword}" requested via Business Entity Keywords`);
    setDeleteTarget(null);
  };
  const statusForKeyword = (kwId: string) => {
    const latest = pendingRequests.filter(r => r.keywordId === kwId).slice(-1)[0];
    return latest ? STATUS_CHIP[latest.status] : null;
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: isDarkMode ? '#0F0E24' : '#F8FAFC'
  }} data-id="element-3163" data-mp="114owvnj7jfu5">
      <TopBar onMenuClick={() => setMobileOpen(!mobileOpen)} onLogout={onLogout} data-id="element-3164" data-mp="1f00qfrlmn09h" />
      <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)} activePage="business-keyword-config" data-id="element-3165" data-mp="1b2mhm11uyui0" />

      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      width: {
        sm: 'calc(100% - 240px)'
      },
      mt: 8
    }} data-id="element-3166" data-mp="34hee93j6wuim">
        <Container maxWidth="xl" data-id="element-3167" data-mp="39giq61xi16cn">
          {/* Header */}
          <Box sx={{
          mb: 3
        }} data-id="element-3168" data-mp="3ead74xlpz781">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 1.5
          }} data-id="element-3169" data-mp="3eber5rkpr41w">
              <Icon icon="lucide:list-filter" width={24} color="#5B4EF0" data-id="element-3170" data-mp="3j7vc6cwm92ar" />
              <Box data-id="element-3171" data-mp="2llo9x9nsbm1k">
                <Typography variant="h5" fontWeight={800} data-id="element-3172" data-mp="2lm7iw0vgj03g">Business Entity Keywords</Typography>
                <Typography variant="body2" color="text.secondary" data-id="element-3173" data-mp="2qk9c3emlkfsn">
                  Global stop-list for detecting commercial names in Remitter / Beneficiary fields
                </Typography>
              </Box>
            </Box>
            <Alert severity="info" icon={<Icon icon="lucide:info" width={18} data-id="element-3175" data-mp="artjy9hwerk3" />} sx={{
            borderRadius: 2,
            fontSize: '0.85rem'
          }} data-id="element-3174" data-mp="2zi2wpsb01kzz">
              Additions and deletions are sent to the checker for approval before they take effect globally.
            </Alert>
          </Box>

          <Paper elevation={0} sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4
        }} data-id="element-3176" data-mp="1va44o6j1kpnw">
            {/* Panel header */}
            <Box sx={{
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: isDarkMode ? 'rgba(91,78,240,0.08)' : 'rgba(91,78,240,0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }} data-id="element-3177" data-mp="1qht6b83y9bxg">
              <Box data-id="element-3178" data-mp="1bozmxblk4bni">
                <Typography fontWeight={800} fontSize="0.95rem" data-id="element-3179" data-mp="17ks05jynt5rx">Keywords</Typography>
                <Typography variant="caption" color="text.secondary" data-id="element-3180" data-mp="12lyjjio2synj">
                  {keywords.length} keyword{keywords.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Button variant="contained" size="small" startIcon={<Icon icon="lucide:plus" width={14} data-id="element-3182" data-mp="3g4mf8ss0ikvc" />} onClick={() => setAddOpen(true)} sx={{
              background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
              fontWeight: 700,
              fontSize: '0.8rem',
              borderRadius: 2
            }} data-id="element-3181" data-mp="2wzzqyrxoytz8">
                Add Keyword
              </Button>
            </Box>

            {keywords.length === 0 ? <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            gap: 1.5
          }} data-id="element-3183" data-mp="3c397ue99u9mb">
                <Icon icon="lucide:search-x" width={40} color="#9CA3AF" data-id="element-3184" data-mp="7oj8cp8q5082" />
                <Typography color="text.secondary" fontWeight={600} data-id="element-3185" data-mp="7moutvzs8ae0">No keywords yet</Typography>
                <Typography variant="caption" color="text.secondary" sx={{
              textAlign: 'center',
              maxWidth: 280
            }} data-id="element-3186" data-mp="3pwidc3u3mazk">
                  Add keywords to block commercial names in Remitter / Beneficiary fields
                </Typography>
                <Button variant="outlined" size="small" startIcon={<Icon icon="lucide:plus" width={14} data-id="element-3188" data-mp="2bub7z1lolt9w" />} onClick={() => setAddOpen(true)} sx={{
              mt: 1,
              borderRadius: 2
            }} data-id="element-3187" data-mp="2je28jd1ztg00">
                  Add First Keyword
                </Button>
              </Box> : <TableContainer data-id="element-3189" data-mp="2kt512hzt01ip">
                <Table data-id="element-3190" data-mp="2fw68w4zpa4n5">
                  <TableHead data-id="element-3191" data-mp="2fxpp5vlcwfox">
                    <TableRow sx={{
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
                }} data-id="element-3192" data-mp="2fubpiv42ukl6">
                      {['Keyword', 'Added By', 'Added On', ''].map(h => <TableCell key={h} sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    py: 1.5
                  }} data-id="element-3193" data-mp="1o732ojo96k7v">
                          {h}
                        </TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody data-id="element-3194" data-mp="19dzlidekq8ho">
                    {keywords.map(kw => {
                  const chip = statusForKeyword(kw.id);
                  return <TableRow key={kw.id} sx={{
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(91,78,240,0.04)' : 'rgba(91,78,240,0.02)'
                    }
                  }} data-id="element-3195" data-mp="jx0f3qx62m6a">
                          <TableCell data-id="element-3196" data-mp="sw3ykw08x3lb">
                            <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }} data-id="element-3197" data-mp="sv2jmgg3zwyb">
                              <Icon icon="lucide:tag" width={14} color="#9CA3AF" data-id="element-3198" data-mp="xvgsg0pjbaxe" />
                              <Typography fontWeight={700} fontSize="0.875rem" sx={{
                          letterSpacing: 0.5
                        }} data-id="element-3199" data-mp="12utggos56o1k">{kw.keyword}</Typography>
                              {chip && <Chip label={chip.label} size="small" sx={{
                          bgcolor: chip.bg,
                          color: chip.color,
                          fontWeight: 700,
                          fontSize: '0.65rem'
                        }} data-id="element-3200" data-mp="16uc3bq3ix971" />}
                            </Box>
                          </TableCell>
                          <TableCell data-id="element-3201" data-mp="1gnsyaxfecuca"><Typography fontSize="0.8rem" color="text.secondary" data-id="element-3202" data-mp="1lkro53s12f4o">{kw.addedBy}</Typography></TableCell>
                          <TableCell data-id="element-3203" data-mp="1qjmf4t05f9zv"><Typography fontSize="0.8rem" color="text.secondary" data-id="element-3204" data-mp="1qjcmexnpyoqh">{kw.addedOn}</Typography></TableCell>
                          <TableCell align="right" data-id="element-3205" data-mp="3b2y9cq2ntmja">
                            <Tooltip title="Request deletion" data-id="element-3206" data-mp="3azkb9u113zhn">
                              <span data-id="element-3207" data-mp="3618zhdr6bews">
                                <IconButton size="small" onClick={() => setDeleteTarget(kw)} disabled={!!chip && chip === STATUS_CHIP.PENDING_CHECKER} sx={{
                            color: '#EF4444',
                            '&:hover': {
                              bgcolor: 'rgba(239,68,68,0.08)'
                            }
                          }} data-id="element-3208" data-mp="313qz4br8280e">
                                  <Icon icon="lucide:trash-2" width={15} data-id="element-3209" data-mp="3tso4xfe8ycc9" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>;
                })}
                  </TableBody>
                </Table>
              </TableContainer>}
          </Paper>

          <Typography fontWeight={800} fontSize="0.95rem" sx={{
          mb: 1.5
        }} data-id="element-3210" data-mp="ge9zhxa463m2">Pending Keyword Requests</Typography>
          {pendingRequests.length === 0 ? <Paper elevation={0} sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          p: 3,
          textAlign: 'center'
        }} data-id="element-3211" data-mp="3tfunu5abr2hh">
              <Typography variant="body2" color="text.secondary" data-id="element-3212" data-mp="3pfkhgrimea23">No pending or rejected keyword requests.</Typography>
            </Paper> : <TableContainer component={Paper} elevation={0} sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: 'hidden'
        }} data-id="element-3213" data-mp="76jqideoyse3">
              <Table data-id="element-3214" data-mp="g89pq5etevmf">
                <TableHead data-id="element-3215" data-mp="g7rnms5h1n6l">
                  <TableRow sx={{
                bgcolor: isDarkMode ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.05)'
              }} data-id="element-3216" data-mp="c7z8fwlkois9">
                    {['Action', 'Keyword', 'Requested By', 'Requested At', 'Status'].map(h => <TableCell key={h} sx={{
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 1.5
                }} data-id="element-3217" data-mp="l0krldt48rhm">{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-3218" data-mp="v0bht3an8kbw">
                  {pendingRequests.map(r => <TableRow key={r.id} data-id="element-3219" data-mp="1ellj38zspp8u">
                      <TableCell data-id="element-3220" data-mp="1ilvookjoedn6"><Chip label={r.action} size="small" sx={{
                    fontWeight: 700,
                    bgcolor: r.action === 'ADD' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                    color: r.action === 'ADD' ? '#059669' : '#EF4444'
                  }} data-id="element-3221" data-mp="1illyavupy4gy" /></TableCell>
                      <TableCell data-id="element-3222" data-mp="19k4k5j0vgcuw"><Typography fontWeight={700} fontSize="0.875rem" data-id="element-3223" data-mp="19jw63nt0j1i4">{r.keyword}</Typography></TableCell>
                      <TableCell data-id="element-3224" data-mp="uuqa08ph1reg"><Typography fontSize="0.8rem" data-id="element-3225" data-mp="uv015zm964lq">{r.requestedBy}</Typography></TableCell>
                      <TableCell data-id="element-3226" data-mp="zokp0yu4ti77"><Typography fontSize="0.8rem" data-id="element-3227" data-mp="zob0zgsndl45">{new Date(r.requestedAt).toLocaleString()}</Typography></TableCell>
                      <TableCell data-id="element-3228" data-mp="lts4tn3ardua"><Chip label={STATUS_CHIP[r.status]?.label || r.status} size="small" sx={{
                    bgcolor: STATUS_CHIP[r.status]?.bg,
                    color: STATUS_CHIP[r.status]?.color,
                    fontWeight: 700
                  }} data-id="element-3229" data-mp="qupmlykea5v9" /></TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>}
        </Container>
      </Box>

      {/* Add Keyword Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{
      sx: {
        borderRadius: 3
      }
    }} data-id="element-3230" data-mp="3p6uj8vyr9zzb">
        <DialogTitle sx={{
        fontWeight: 800,
        pb: 1
      }} data-id="element-3231" data-mp="3k8j90k4jtngk">Add Keyword</DialogTitle>
        <DialogContent data-id="element-3232" data-mp="3f8ww6bsmu98a">
          <TextField autoFocus label="Keyword" fullWidth size="small" value={newKw} onChange={e => setNewKw(e.target.value.toUpperCase())} placeholder="e.g. SONS" onKeyDown={e => e.key === 'Enter' && handleAdd()} inputProps={{
          style: {
            fontWeight: 700,
            letterSpacing: 1
          }
        }} data-id="element-3233" data-mp="3f9g20u50d75y" />
          <Alert severity="info" icon={<Icon icon="lucide:clock" width={16} data-id="element-3235" data-mp="1t8nzr6pc454" />} sx={{
          mt: 2,
          borderRadius: 2,
          fontSize: '0.8rem'
        }} data-id="element-3234" data-mp="1sgsegd3u6eu">
            This will be sent to the checker for approval before the keyword becomes active.
          </Alert>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2.5,
        gap: 1
      }} data-id="element-3236" data-mp="121yn2abg15mp">
          <Button onClick={() => setAddOpen(false)} sx={{
          borderRadius: 2
        }} data-id="element-3237" data-mp="1239nc05ojv92">Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!newKw.trim()} sx={{
          background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
          fontWeight: 700,
          borderRadius: 2
        }} data-id="element-3238" data-mp="171b8cqmo24n6">
            Send for Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{
      sx: {
        borderRadius: 3
      }
    }} data-id="element-3239" data-mp="1pqhu6mirbcd7">
        <DialogTitle sx={{
        fontWeight: 800
      }} data-id="element-3240" data-mp="1ur5hoy7s1b2o">Request Keyword Deletion?</DialogTitle>
        <DialogContent data-id="element-3241" data-mp="1zoxbjxrzf79i">
          <Typography variant="body2" color="text.secondary" data-id="element-3242" data-mp="1zncp6vftmumq">
            A deletion request for <strong data-id="element-3243" data-mp="e5xsswd5upl">{deleteTarget?.keyword}</strong> will be sent to the checker for approval. It stays active until then.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2.5,
        gap: 1
      }} data-id="element-3244" data-mp="1vrgtcw045atz">
          <Button onClick={() => setDeleteTarget(null)} sx={{
          borderRadius: 2
        }} data-id="element-3245" data-mp="1qtfaab2otbii">Cancel</Button>
          <Button variant="contained" color="error" onClick={() => deleteTarget && handleDelete(deleteTarget)} sx={{
          fontWeight: 700,
          borderRadius: 2
        }} data-id="element-3246" data-mp="1qvt5cjxdanq2">
            Send for Approval
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default BusinessKeywordConfig;